import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Tabs,
  Select,
  InputNumber,
  Tag,
  Image,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { apiService } from '../../../services/api';
import type { CustomList, CustomListAppItem, AdminAppListItem } from '../../../types';

const { Title } = Typography;
const { TabPane } = Tabs;

interface CustomListFormData {
  name: string;
  show_title?: boolean;
}

const CustomLists: React.FC = () => {
  const [customLists, setCustomLists] = useState<CustomList[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingList, setEditingList] = useState<CustomList | null>(null);
  const [form] = Form.useForm();

  // APP管理相关状态
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [selectedListName, setSelectedListName] = useState<string>('');
  const [listApps, setListApps] = useState<CustomListAppItem[]>([]);
  const [allApps, setAllApps] = useState<AdminAppListItem[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [addAppModalVisible, setAddAppModalVisible] = useState(false);
  const [addAppForm] = Form.useForm();
  const [appPagination, setAppPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    loadCustomLists();
  }, []);

  useEffect(() => {
    if (selectedListId) {
      loadListApps(selectedListId);
    }
  }, [selectedListId]);

  const loadCustomLists = async () => {
    try {
      setLoading(true);
      const response = await apiService.adminGetCustomLists();
      if (response.code === 0) {
        setCustomLists(response.data);
      } else {
        message.error(response.message || '获取自定义列表失败');
      }
    } catch (error: any) {
      message.error('获取自定义列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadListApps = async (listId: number, page = 1) => {
    try {
      setAppsLoading(true);
      const response = await apiService.adminGetCustomListDetail(listId, {
        page: page,
        per_page: appPagination.pageSize,
      });
      if (response.code === 0) {
        setListApps(response.data.apps);
        setAppPagination({
          current: page,
          pageSize: appPagination.pageSize,
          total: response.data.pagination.total_items,
        });
      } else {
        message.error(response.message || '获取列表APP失败');
      }
    } catch (error: any) {
      message.error('获取列表APP失败');
    } finally {
      setAppsLoading(false);
    }
  };

  const loadAllApps = async (search?: string) => {
    try {
      const response = await apiService.adminGetApps({
        page: 1,
        per_page: 100,
        search: search,
      });
      if (response.code === 0) {
        setAllApps(response.data.items);
      }
    } catch (error: any) {
      message.error('获取APP列表失败');
    }
  };

  const handleCreate = () => {
    setEditingList(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (list: CustomList) => {
    setEditingList(list);
    setModalVisible(true);
    form.setFieldsValue({
      name: list.name,
      show_title: list.show_title,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await apiService.adminDeleteCustomList(id);
      if (response.code === 0) {
        message.success('删除成功');
        loadCustomLists();
        if (selectedListId === id) {
          setSelectedListId(null);
          setSelectedListName('');
        }
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error: any) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: CustomListFormData) => {
    try {
      if (editingList) {
        const response = await apiService.adminUpdateCustomList(editingList.id, values);
        if (response.code === 0) {
          message.success('更新成功');
          setModalVisible(false);
          loadCustomLists();
        } else {
          message.error(response.message || '更新失败');
        }
      } else {
        const response = await apiService.adminCreateCustomList(values);
        if (response.code === 0) {
          message.success('创建成功');
          setModalVisible(false);
          loadCustomLists();
        } else {
          message.error(response.message || '创建失败');
        }
      }
    } catch (error: any) {
      message.error(editingList ? '更新失败' : '创建失败');
    }
  };

  const handleSelectList = (list: CustomList) => {
    setSelectedListId(list.id);
    setSelectedListName(list.name);
  };

  const handleAddApp = () => {
    if (!selectedListId) {
      message.warning('请先选择一个列表');
      return;
    }
    setAddAppModalVisible(true);
    addAppForm.resetFields();
    loadAllApps();
  };

  const handleAddAppSubmit = async (values: { app_id: number; sort_order?: number }) => {
    try {
      const response = await apiService.adminAddAppToCustomList(selectedListId!, {
        app_id: values.app_id,
        sort_order: values.sort_order || 0,
      });
      if (response.code === 0) {
        message.success('添加成功');
        setAddAppModalVisible(false);
        loadListApps(selectedListId!);
      } else {
        message.error(response.message || '添加失败');
      }
    } catch (error: any) {
      message.error('添加失败');
    }
  };

  const handleRemoveApp = async (appId: number) => {
    try {
      const response = await apiService.adminRemoveAppFromCustomList(selectedListId!, appId);
      if (response.code === 0) {
        message.success('移除成功');
        loadListApps(selectedListId!);
      } else {
        message.error(response.message || '移除失败');
      }
    } catch (error: any) {
      message.error('移除失败');
    }
  };

  const handleUpdateSortOrder = async (appId: number, sortOrder: number) => {
    try {
      const response = await apiService.adminUpdateCustomListAppSortOrder(selectedListId!, appId, sortOrder);
      if (response.code === 0) {
        message.success('更新成功');
        loadListApps(selectedListId!);
      } else {
        message.error(response.message || '更新失败');
      }
    } catch (error: any) {
      message.error('更新失败');
    }
  };

  const listColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '列表名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <UnorderedListOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: '前台显示名称',
      dataIndex: 'show_title',
      key: 'show_title',
      width: 140,
      render: (showTitle: boolean) => (
        <Tag color={showTitle ? 'green' : 'default'}>
          {showTitle ? '显示' : '隐藏'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_: any, record: CustomList) => (
        <Space>
          <Button
            type="link"
            icon={<AppstoreOutlined />}
            onClick={() => handleSelectList(record)}
            size="small"
          >
            管理APP
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个列表吗？"
            description="删除后不可恢复，请谨慎操作。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const appColumns = [
    {
      title: '图标',
      dataIndex: 'icon_url',
      key: 'icon_url',
      width: 80,
      render: (url: string) => (
        <Image
          src={url}
          alt="app icon"
          width={50}
          height={50}
          style={{ borderRadius: 8 }}
          fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yNSAyNUMxOS40NzcgMjUgMTUgMjkuNDc3IDE1IDM1QzE1IDQwLjUyMyAxOS40NzcgNDUgMjUgNDVDMzAuNTIzIDQ1IDM1IDQwLjUyMyAzNSAzNUMzNSA5LjQ3NyAzMC41MjMgNSAyNSA1WiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K"
        />
      ),
    },
    {
      title: 'APP名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: CustomListAppItem) => (
        <Space>
          <span>{text}</span>
          <Tag color="blue">{record.category.name}</Tag>
        </Space>
      ),
    },
    {
      title: '排序序号',
      key: 'sort_order',
      width: 150,
      render: (_: any, record: CustomListAppItem) => {
        return (
          <InputNumber
            min={0}
            value={record.sort_order}
            style={{ width: '100%' }}
            onPressEnter={(e: any) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value)) {
                handleUpdateSortOrder(record.id, value);
              }
            }}
            onBlur={(e: any) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value)) {
                handleUpdateSortOrder(record.id, value);
              }
            }}
          />
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_: any, record: CustomListAppItem) => (
        <Popconfirm
          title="确定要从列表中移除这个APP吗？"
          onConfirm={() => handleRemoveApp(record.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button
            type="link"
            danger
            icon={<CloseOutlined />}
            size="small"
          >
            移除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={2} style={{ margin: 0 }}>
              自定义列表管理
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              新建列表
            </Button>
          </div>
        </Col>

        <Col span={24}>
          <Tabs defaultActiveKey="lists">
            <TabPane tab="列表管理" key="lists">
              <Table
                columns={listColumns}
                dataSource={customLists}
                rowKey="id"
                loading={loading}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 条记录`,
                }}
              />
            </TabPane>

            {selectedListId && (
              <TabPane tab={`${selectedListName} - APP管理`} key="apps">
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddApp}
                  >
                    添加APP
                  </Button>
                </div>
                <Table
                  columns={appColumns}
                  dataSource={listApps}
                  rowKey="id"
                  loading={appsLoading}
                  pagination={{
                    current: appPagination.current,
                    pageSize: appPagination.pageSize,
                    total: appPagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条记录`,
                    onChange: (page, pageSize) => {
                      setAppPagination({
                        ...appPagination,
                        current: page,
                        pageSize: pageSize || appPagination.pageSize,
                      });
                      loadListApps(selectedListId, page);
                    },
                  }}
                />
              </TabPane>
            )}
          </Tabs>
        </Col>
      </Row>

      {/* 创建/编辑列表Modal */}
      <Modal
        title={editingList ? '编辑列表' : '新建列表'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="列表名称"
            rules={[
              { required: true, message: '请输入列表名称' },
              { min: 2, message: '列表名称至少2个字符' },
              { max: 50, message: '列表名称最多50个字符' },
            ]}
          >
            <Input placeholder="请输入列表名称" />
          </Form.Item>

          <Form.Item
            name="show_title"
            label="前台显示列表名称"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingList ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加APP Modal */}
      <Modal
        title="添加APP到列表"
        open={addAppModalVisible}
        onCancel={() => setAddAppModalVisible(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form
          form={addAppForm}
          layout="vertical"
          onFinish={handleAddAppSubmit}
        >
          <Form.Item
            name="app_id"
            label="选择APP"
            rules={[{ required: true, message: '请选择APP' }]}
          >
            <Select
              placeholder="请选择APP"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onSearch={(value) => {
                loadAllApps(value);
              }}
              options={allApps.map(app => ({
                label: app.name,
                value: app.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="sort_order"
            label="排序序号（越大越靠前）"
            initialValue={0}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="请输入排序序号"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setAddAppModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomLists;
